// Aba "Semanas": uma etapa por linha recolhível, a semana de hoje aberta e destacada.
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Collapse, Form, Input, Modal, Progress, Select, Table, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  criarTarefa, salvarEtapa, salvarTarefa, type Etapa, type StatusEtapa, type Tarefa,
} from './api';
import { CampoData, nomeDoPapel, Rotulo, type Contexto } from './comum';
import InformacoesTabela from './InformacoesTabela';
import { PAGINAS } from './paginas';
import {
  dia, opcoesStatusTarefa, periodoCurto, podeDecidirEtapa, podeMudarTarefa, progresso, ROTULO_ETAPA, ROTULO_TAREFA, semanaAtual, tarefaAtrasada,
  temPaginaEspecial, travaValida,
} from './regras';
import { fmtDataBr } from '../../utils/dataFmt';

const { Text, Paragraph } = Typography;

const OPCOES_ETAPA = (Object.keys(ROTULO_ETAPA) as StatusEtapa[]).map((v) => ({ value: v, label: ROTULO_ETAPA[v].texto }));

interface FormTarefa { titulo: string; descricao: string; prazo: string | null; papel_responsavel: string | null }

export default function AbaSemanas({ ctx }: { ctx: Contexto }) {
  const { dados, hoje, salvar } = ctx;
  const { etapas, permissoes } = dados;
  const ligado = dados.ciclo.status === 'ligado';
  const navigate = useNavigate();
  const atual = useMemo(() => semanaAtual(etapas, hoje), [etapas, hoje]);
  const idAtual = etapas.find((e) => e.numero === atual.numero)?.id;
  const [abertas, setAbertas] = useState<string[]>(idAtual ? [String(idAtual)] : []);
  useEffect(() => { if (idAtual) setAbertas((a) => (a.length ? a : [String(idAtual)])); }, [idAtual]);

  // Modal da tarefa (gestor): editar ou criar.
  const [tarefaEdit, setTarefaEdit] = useState<{ etapa: Etapa; tarefa: Tarefa | null } | null>(null);
  const [formT, setFormT] = useState<FormTarefa>({ titulo: '', descricao: '', prazo: null, papel_responsavel: null });
  const [gravandoT, setGravandoT] = useState(false);
  const opcoesPapel = dados.papeis.map((p) => ({ value: p.papel, label: p.nome ? `${p.rotulo} — ${p.nome}` : p.rotulo }));

  // Modal da decisão / trava (dono da decisão ou gestor).
  const [decisao, setDecisao] = useState<{ etapa: Etapa; modo: 'decisao' | 'trava' } | null>(null);
  const [texto, setTexto] = useState('');
  const [dataDecisao, setDataDecisao] = useState<string | null>(null);
  const [gravandoD, setGravandoD] = useState(false);
  const [erroD, setErroD] = useState<string | null>(null);

  const abrirTarefa = (etapa: Etapa, tarefa: Tarefa | null) => {
    setTarefaEdit({ etapa, tarefa });
    setFormT({
      titulo: tarefa?.titulo || '', descricao: tarefa?.descricao || '', prazo: dia(tarefa?.prazo) || null,
      papel_responsavel: tarefa?.papel_responsavel || null,
    });
  };
  const gravarTarefa = async () => {
    if (!tarefaEdit) return;
    if (!formT.titulo.trim()) return;
    setGravandoT(true);
    const campos = {
      titulo: formT.titulo.trim(), descricao: formT.descricao.trim() || null, prazo: formT.prazo, papel_responsavel: formT.papel_responsavel,
    };
    const { etapa, tarefa } = tarefaEdit;
    const ok = await salvar(
      () => (tarefa ? salvarTarefa(tarefa.id, tarefa.revisao, campos) : criarTarefa(etapa.id, campos)),
      tarefa ? 'Tarefa salva.' : 'Tarefa criada.',
    );
    setGravandoT(false);
    if (ok) setTarefaEdit(null);
  };

  const abrirDecisao = (etapa: Etapa, modo: 'decisao' | 'trava') => {
    setDecisao({ etapa, modo });
    setErroD(null);
    setTexto(modo === 'decisao' ? (etapa.decisao_texto || '') : (etapa.motivo_trava || ''));
    setDataDecisao(modo === 'decisao' ? (dia(etapa.decisao_em) || hoje) : null);
  };
  const gravarDecisao = async () => {
    if (!decisao) return;
    const { etapa, modo } = decisao;
    if (modo === 'trava') {
      const e = travaValida('travada', texto);
      if (e) { setErroD(e); return; }
    }
    setGravandoD(true);
    const ok = await salvar(
      () => (modo === 'decisao'
        ? salvarEtapa(etapa.id, etapa.revisao, { decisao_texto: texto.trim(), decisao_em: dataDecisao || hoje })
        : salvarEtapa(etapa.id, etapa.revisao, { status: 'travada', motivo_trava: texto.trim() })),
      modo === 'decisao' ? 'Decisão registrada.' : 'Semana marcada como travada.',
    );
    setGravandoD(false);
    if (ok) setDecisao(null);
  };

  if (!etapas.length) return <Alert type="info" showIcon message="Este ciclo ainda não tem semanas." />;

  const corpo = (e: Etapa) => {
    const podeDecidir = podeDecidirEtapa(permissoes, e);
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          <div><Rotulo>O que fazer</Rotulo><Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{e.o_que_fazer || '—'}</Paragraph></div>
          <div><Rotulo>Como fazer</Rotulo><Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{e.como_fazer || '—'}</Paragraph></div>
          <div><Rotulo>Entrega</Rotulo><Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{e.entrega || '—'}</Paragraph></div>
          <div>
            <Rotulo>Quem faz</Rotulo><div>{e.quem_faz || '—'}</div>
            <Rotulo>Tempo</Rotulo><div>{e.tempo_estimado || '—'}</div>
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <Text strong>Tarefas</Text>
            {permissoes.pode_gerir && <Button size="small" onClick={() => abrirTarefa(e, null)}>Adicionar tarefa</Button>}
          </div>
          <Table<Tarefa>
            size="small" pagination={false} rowKey="id" dataSource={e.tarefas} scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'Sem tarefas nesta semana.' }}
            columns={[
              {
                title: 'Tarefa', dataIndex: 'titulo',
                render: (_, t) => (
                  <div>
                    <div>
                      {t.titulo}
                      {temPaginaEspecial(t) && <Tag color="blue" style={{ marginLeft: 8 }}>{PAGINAS[t.pagina]?.titulo || 'Página'}</Tag>}
                    </div>
                    {t.descricao && <Text type="secondary" style={{ fontSize: 12 }}>{t.descricao}</Text>}
                  </div>
                ),
              },
              { title: 'Responsável', width: 230, render: (_, t) => nomeDoPapel(dados, t.papel_responsavel) },
              {
                title: 'Prazo', width: 120,
                render: (_, t) => (tarefaAtrasada(t, hoje, ligado)
                  ? <Text type="danger" strong>{fmtDataBr(t.prazo)}</Text>
                  : fmtDataBr(t.prazo)),
              },
              {
                title: 'Situação', width: 160,
                render: (_, t) => {
                  if (podeMudarTarefa(permissoes, t, ligado)) {
                    return (
                      <Select
                        size="small" style={{ width: 140 }} showSearch optionFilterProp="label"
                        options={opcoesStatusTarefa(permissoes.pode_gerir, t.status).map((v) => ({ value: v, label: ROTULO_TAREFA[v] }))} value={t.status}
                        onChange={(v) => { void salvar(() => salvarTarefa(t.id, t.revisao, { status: v })); }}
                      />
                    );
                  }
                  return <Tag>{ROTULO_TAREFA[t.status]}</Tag>;
                },
              },
              {
                title: '', width: 190,
                render: (_, t) => (
                  <span>
                    <Button type="link" size="small" onClick={() => navigate(`/administrativo/plano-acao/tarefa/${t.id}`)}>Abrir página</Button>
                    {permissoes.pode_gerir && <Button type="link" size="small" onClick={() => abrirTarefa(e, t)}>Editar</Button>}
                  </span>
                ),
              },
            ]}
          />
        </div>

        <div>
          <Text strong>Informações necessárias</Text>
          <div style={{ marginTop: 6 }}><InformacoesTabela itens={e.informacoes} ctx={ctx} /></div>
        </div>

        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}>
            <Text strong>Decisão{e.decisao_numero ? ` ${String(e.decisao_numero).padStart(2, '0')}` : ''}</Text>
            {e.decisao_titulo && <div style={{ color: '#8c8c8c', fontSize: 12 }}>{e.decisao_titulo}</div>}
            <div style={{ margin: '8px 0' }}>
              {e.decisao_texto
                ? <><Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 2 }}>{e.decisao_texto}</Paragraph><Text type="secondary" style={{ fontSize: 12 }}>Registrada em {fmtDataBr(e.decisao_em)}{e.decisao_por ? ` por ${e.decisao_por}` : ''}</Text></>
                : <Text type="secondary">Ainda sem decisão registrada. Quem decide: {nomeDoPapel(dados, e.papel_dono_decisao)}.</Text>}
            </div>
            {podeDecidir && <Button size="small" type="primary" onClick={() => abrirDecisao(e, 'decisao')}>{e.decisao_texto ? 'Alterar decisão' : 'Registrar decisão'}</Button>}
          </div>

          <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}>
            <Text strong>Travada?</Text>
            <div style={{ margin: '8px 0' }}>
              {e.status === 'travada'
                ? <Text type="danger">{e.motivo_trava || 'Sem motivo escrito.'}</Text>
                : <Text type="secondary">Não. Se algo impedir a semana de andar, marque aqui e escreva o motivo.</Text>}
            </div>
            {podeDecidir && (e.status === 'travada'
              ? <Button size="small" onClick={() => { void salvar(() => salvarEtapa(e.id, e.revisao, { status: 'em_andamento', motivo_trava: null }), 'Semana destravada.'); }}>Destravar</Button>
              : <Button size="small" danger onClick={() => abrirDecisao(e, 'trava')}>Marcar como travada</Button>)}
            {permissoes.pode_gerir && e.status !== 'travada' && (
              <div style={{ marginTop: 10 }}>
                <Rotulo>Situação da semana</Rotulo>
                <Select
                  size="small" style={{ width: 180 }} showSearch optionFilterProp="label"
                  options={OPCOES_ETAPA.filter((o) => o.value !== 'travada')} value={e.status}
                  onChange={(v) => { void salvar(() => salvarEtapa(e.id, e.revisao, { status: v })); }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Collapse
        activeKey={abertas} onChange={(k) => setAbertas(Array.isArray(k) ? k.map(String) : [String(k)])}
        items={etapas.map((e) => {
          const p = progresso(e.tarefas);
          const destaque = e.numero === atual.numero;
          return {
            key: String(e.id),
            style: destaque ? { background: '#f0f5ff' } : undefined,
            label: (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <Text strong>S{e.numero}</Text>
                <Text type="secondary">{periodoCurto(e.inicio, e.fim)}</Text>
                <Text strong>{e.titulo}</Text>
                {destaque && <Tag color="blue">Esta semana</Tag>}
                {e.decisao_numero != null && <Tag>Decisão {String(e.decisao_numero).padStart(2, '0')}</Tag>}
                <Text type="secondary" style={{ fontSize: 12 }}>Dono: {nomeDoPapel(dados, e.papel_dono_decisao)}</Text>
                <Tag color={ROTULO_ETAPA[e.status]?.cor}>{ROTULO_ETAPA[e.status]?.texto || e.status}</Tag>
                {p.total > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 130 }}>
                    <Progress percent={p.pct} size="small" showInfo={false} style={{ width: 70, margin: 0 }} />
                    <Text type="secondary" style={{ fontSize: 12 }}>{p.feitas} de {p.total}</Text>
                  </span>
                )}
              </div>
            ),
            children: corpo(e),
          };
        })}
      />

      <Modal
        open={!!tarefaEdit} title={tarefaEdit?.tarefa ? 'Editar tarefa' : 'Nova tarefa'} onCancel={() => setTarefaEdit(null)}
        onOk={gravarTarefa} okText="Salvar" cancelText="Cancelar" okButtonProps={{ disabled: !formT.titulo.trim() }}
        confirmLoading={gravandoT} width="94vw" style={{ maxWidth: 720, top: 16 }} destroyOnHidden
      >
        <Form layout="vertical">
          <Form.Item label="Tarefa" required><Input value={formT.titulo} onChange={(ev) => setFormT({ ...formT, titulo: ev.target.value })} /></Form.Item>
          <Form.Item label="Detalhe"><Input.TextArea rows={3} value={formT.descricao} onChange={(ev) => setFormT({ ...formT, descricao: ev.target.value })} /></Form.Item>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <Form.Item label="Prazo"><CampoData valor={formT.prazo} onChange={(v) => setFormT({ ...formT, prazo: v })} /></Form.Item>
            <Form.Item label="Responsável">
              <Select
                showSearch optionFilterProp="label" options={opcoesPapel} value={formT.papel_responsavel || undefined}
                placeholder="Escolha quem faz" onChange={(v) => setFormT({ ...formT, papel_responsavel: v })}
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <Modal
        open={!!decisao} onCancel={() => setDecisao(null)} onOk={gravarDecisao} okText="Salvar" cancelText="Cancelar" confirmLoading={gravandoD}
        title={decisao?.modo === 'decisao' ? 'Decisão desta semana' : 'Por que a semana está travada?'}
        width="94vw" style={{ maxWidth: 640, top: 16 }} destroyOnHidden
      >
        <Form layout="vertical">
          <Form.Item label={decisao?.modo === 'decisao' ? 'O que foi decidido' : 'Motivo'} validateStatus={erroD ? 'error' : undefined} help={erroD || undefined}>
            <Input.TextArea rows={5} value={texto} onChange={(ev) => { setTexto(ev.target.value); setErroD(null); }} />
          </Form.Item>
          {decisao?.modo === 'decisao' && <Form.Item label="Data da decisão"><CampoData valor={dataDecisao} onChange={setDataDecisao} /></Form.Item>}
        </Form>
      </Modal>
    </>
  );
}
